import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, TextField, List, ListItem, ListItemButton,
  ListItemText, CircularProgress, Box, Typography, IconButton, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useChat } from '../../context/chatContext';
import { searchChats } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

// Interfaces for the raw API response
interface Highlight {
  type: 'event' | 'impression' | 'schema';
  snippet: string;
  timestamp: string | null;
}

interface ApiSearchResult {
  chat_id: string;
  title: string | null;
  highlights: Highlight[];
}

// A new interface for our flattened and filtered results
interface Match {
  chat_id: string;
  chat_title: string;
  snippet: string;
  type: string;
  // A unique key for React rendering
  id: string;
}


const SearchModal: React.FC<SearchModalProps> = ({ open, onClose }) => {
  const { userId, setSessionId, setToken } = useChat();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Match[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setError(null);
    setLoading(false);
    onClose();
  };

  const performSearch = async (currentQuery: string) => {
    if (!currentQuery.trim() || !userId) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await searchChats(userId, currentQuery, 0, 50, undefined, false, setSessionId, setToken);

      // Filter out chats with "unknown" ID and flatten the highlights into a single list
      const matches: Match[] = response.results
        .filter((chat: ApiSearchResult) => chat.chat_id !== 'unknown')
        .flatMap((chat: ApiSearchResult) =>
          chat.highlights.map((highlight, index) => ({
            chat_id: chat.chat_id,
            chat_title: chat.title || `${new Date(chat.highlights[0]?.timestamp || Date.now()).toLocaleDateString()}`,
            snippet: highlight.snippet,
            type: highlight.type,
            id: `${chat.chat_id}-${index}`, // Create a stable unique key
          }))
        );

      setResults(matches);

    } catch (err: any) {
      setError(err.message || 'Failed to fetch search results.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const timerId = setTimeout(() => {
      performSearch(query);
    }, 400);
    return () => clearTimeout(timerId);
  }, [query, open, userId]);

  const handleResultClick = (chatId: string) => {
    navigate(`/chat/${chatId}`);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { height: '75vh' } }}>
      <DialogTitle sx={{ pr: 8 }}>
        Search Conversations
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          margin="dense"
          id="search"
          label="Search..."
          type="search"
          fullWidth
          variant="outlined"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Box sx={{ mt: 2, position: 'relative' }}>
          {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />}
          {error && <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>{error}</Typography>}
          {!loading && !error && query && results.length === 0 && (
            <Typography sx={{ textAlign: 'center', mt: 4 }}>No results found.</Typography>
          )}

          <List>
            {results.map((match) => (
              <ListItem key={match.id} disablePadding>
                <ListItemButton onClick={() => handleResultClick(match.chat_id)}>
                  <ListItemText
                    primary={match.snippet}
                    secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Chip sx={{mr: 1}} label={`${match.chat_title}`} size="small" variant="outlined" />
                          <Chip label={match.type} size="small" variant="outlined" />
                        </Box>
                      }
                    primaryTypographyProps={{
                      style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;